import { signalStore, withState, withMethods, withComputed, patchState } from '@ngrx/signals';
import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, inject, Injector, runInInjectionContext } from '@angular/core';
import { BudgetCounterDto, LoginResponseDto, SimpleDataDto,  TagDto, UserFullDto } from '@dtos';
import { UserNotificationDto } from '@dtos/notifications';
import { Helpers } from '@utils/helpers';
import { Subject } from 'rxjs';
import { UserRole } from '@enums/user.enum';
import { UserClientEntityDto } from '@dtos/users-clients-entities.dto';
import { UserClientStore } from './user-client.state';
import { TransportStore } from './transports.state';
import { DriverStore } from './drivers.state';
import { ImportDataUrlsDto } from '@dtos/import-data.dto';
import { GlobalDataProvider } from '@providers2/globalDataProvider';
import { IncidenceMainType } from '@enums/incidences.enum';
import { ApiService } from '@services/api.service';
import { SessionTracker } from '@services/session-tracker.service';
import { EndPoints } from '@services/EndPoints';

export interface counters{
  drivers: number;
  addicionalsCosts: number;
  invoicesDrivers: number;

}

export class GlobalStateData {
    user: LoginResponseDto = new LoginResponseDto();
    language: string = "es";
    userFull: UserFullDto = new UserFullDto();
    tags: TagDto[] = [];
    menuCollapsed: boolean = false;
    usersAdmin: SimpleDataDto[] = [];
    groups: SimpleDataDto[] = [];
    role: UserRole = UserRole.USER;
    userClient: UserClientEntityDto | null = null;
    importDataUrls: ImportDataUrlsDto = new ImportDataUrlsDto();
    counters: counters | null = null;
    isLoading: boolean = false;
    budgetCounter: BudgetCounterDto = new BudgetCounterDto();
    unreadNotifications: UserNotificationDto[] = [];
    unreadCount: number = 0;
    incidencesTypes: Partial<Record<IncidenceMainType, {label: string, items: {value: string, label: string, hasQuantity?: boolean}[]}>> = {};
    intervalId: any = null;
    rememberMe: boolean = false;
}

const userLoggedSubject = new Subject<boolean>();
let counterIntervalTimer: any = null;
let isCountersLoading = false;
let activeLoadingRequests = 0;
export const GlobalStore = signalStore(
  { 
    providedIn: 'root',
    // Configuración para Redux DevTools
    ...(typeof window !== 'undefined' && (window as any).__REDUX_DEVTOOLS_EXTENSION__ && {
      devtools: {
        name: 'RoadIt Global Store',
        trace: true,
        traceLimit: 25
      }
    })
  },
  withDevtools('GlogalStore'),
  // Estado inicial usando directamente GlobalStateData
  withState(new GlobalStateData()),
  
  // Computed values
  withComputed((state) => ({
    isClient: computed(() => 
    {
      return state.user && state.user().user.role == UserRole.CLIENT_USER
    }),
    isAdmin: computed(() => 
    {
      return state.user && state.user().user.role == UserRole.ADMIN
    }),
    isAuthenticated: computed(() => 
    {
      if(state.user && state.user().token != "") return true;
      return false;

    }
      ),
      
  })),
  
  withMethods((store) => {
    const injector = inject(Injector);
    return {
      token: () => {
        if (store.user && store.user().token != "") {
          const expiration = Helpers.getAnyStorage('roadit_token_expiration');
          if (expiration) {
            const expDate = new Date(expiration);
            const now = new Date();
            if (now.getTime() < expDate.getTime()) {
              return store.user().token;
            }
          }
        }
        return "expired";
    },
    setIsLoading: (isLoading: boolean, forceReset: boolean = false) => {
      if (forceReset) {
        activeLoadingRequests = 0;
        patchState(store, { isLoading: false });
        return;
      }
      if (isLoading) {
        activeLoadingRequests++;
        patchState(store, { isLoading: true });
      } else {
        activeLoadingRequests = Math.max(0, activeLoadingRequests - 1);
        if (activeLoadingRequests === 0) {
          patchState(store, { isLoading: false });
        }
      }
    },
    updateState: (updates: Partial<GlobalStateData>) => {
      const currentState = {
        language: store.language(),
        menuCollapsed: store.menuCollapsed(),
        user: store.user(),
        userFull: store.userFull(),
        tags: store.tags(),
        usersAdmin: store.usersAdmin(),
        groups: store.groups(),
        role: store.role(),
        userClient: store.userClient(),
        importDataUrls: store.importDataUrls(),
        budgetCounter: store.budgetCounter(),
        unreadNotifications: store.unreadNotifications ? store.unreadNotifications() : [],
        unreadCount: store.unreadCount ? store.unreadCount() : 0,
        rememberMe: store.rememberMe(),
        incidencesTypes: store.incidencesTypes(),
        intervalId: store.intervalId(),
        isLoading: store.isLoading(),
        counters: store.counters ? store.counters() : null
      };

      const newState: GlobalStateData = {
        ...currentState,
        ...updates
      };

      patchState(store, newState);
      saveGlobalStoreToStorage(newState);
    },
    loadMasterTypes: async (forceRefresh: boolean = false) => {
      const current = store.incidencesTypes();
      if (!forceRefresh && current && Object.keys(current).length > 0) {
        return;
      }
      const master = injector.get(GlobalDataProvider);
      const types = await master.getTypes(forceRefresh);
      if(types){
        patchState(store, { incidencesTypes: types });
      }
    },
    loadCounters: async () => {
      if (isCountersLoading) return;
      isCountersLoading = true;
      const master = injector.get(GlobalDataProvider);
      try {
        const counters = await master.getBudgetCounters();
        if(counters){
          patchState(store, { budgetCounter: counters });
        }
      } catch (error) {
        patchState(store, { budgetCounter: new BudgetCounterDto() });
      } finally {
        isCountersLoading = false;
      }

      // Solo consultar notificaciones no leídas si el usuario es CLIENT_USER
      if (store.isClient()) {
        try {
          const unread = await master.getUnreadNotifications();
          patchState(store, {
            unreadNotifications: unread.results,
            unreadCount: unread.count
          });
        } catch (error) {
          console.error('Error al cargar notificaciones no leídas en global store:', error);
          patchState(store, { unreadNotifications: [], unreadCount: 0 });
        }
      } else {
        patchState(store, { unreadNotifications: [], unreadCount: 0 });
      }
    },
    markNotificationAsRead: async (notificationId: number): Promise<boolean> => {
      const master = injector.get(GlobalDataProvider);
      try {
        const success = await master.markNotificationAsRead(notificationId);
        if (success) {
          const currentUnread = store.unreadNotifications();
          patchState(store, {
            unreadNotifications: currentUnread.filter(n => n.id !== notificationId),
            unreadCount: Math.max(0, store.unreadCount() - 1)
          });
        }
        return success;
      } catch (error) {
        console.error('Error al marcar notificación como leída en global store:', error);
        return false;
      }
    },
  };
  }),

  withMethods((store) => {
    const injector = inject(Injector);
    return {
      getUserLoggedOutObservable: () => {
        return userLoggedSubject.asObservable();
      },
      
      updateTokens: (token: string, refreshToken?: string) => {
        const expirationTime = new Date();
        expirationTime.setHours(expirationTime.getHours() + 8);
        Helpers.setStorage('roadit_token_expiration', expirationTime.toISOString());

        const currentUser = store.user();
        const updatedUser = new LoginResponseDto({
          ...currentUser,
          token: token,
          ...(refreshToken ? { 'refresh-token': refreshToken } : {})
        });

        store.updateState({ user: updatedUser });
      },
      
      setUser:  async (user: LoginResponseDto, rememberMe: boolean = false, isFromInit: boolean = false) => {
        if(!isFromInit) {
          if (user.token) {
            const expirationTime = new Date();
            expirationTime.setHours(expirationTime.getHours() + 8); // 8 horas por defecto
            
            // Always write to localStorage (shared across tabs)
            Helpers.setStorage('roadit_token_expiration', expirationTime.toISOString());
            // Legacy cleanup: remove stale sessionStorage copy
            Helpers.removeSessionStorage('roadit_token_expiration');
            
            // If not rememberMe, set session tab flag for F5 survival
            if (!rememberMe) {
              Helpers.setSessionStorage('roadit_session_tab_active', 'true');
            }
          }
        }

        store.updateState( { 
          user: user, 
          role : user.user.role as unknown as UserRole,
          rememberMe: rememberMe 
        });

        userLoggedSubject.next(true);
        
        if (counterIntervalTimer) {
          clearInterval(counterIntervalTimer);
        }
        counterIntervalTimer = setInterval(() => { store.loadCounters().then(() => {}); }, 1000 * 60 * 5);
        store.loadCounters().then(() => {});
      },
    
      logout: () => {
        activeLoadingRequests = 0;
        patchState(store, {
          user: new LoginResponseDto(),
          userFull: new UserFullDto(),
          unreadNotifications: [],
          unreadCount: 0,
          rememberMe: false,
          isLoading: false
        });

        if (counterIntervalTimer) {
          clearInterval(counterIntervalTimer);
          counterIntervalTimer = null;
        }
        
        Helpers.clearStorage();
        Helpers.clearSessionStorage();
        
        userLoggedSubject.next(false);
      },
    }
  }),

  withMethods((store) => {
    const injector = inject(Injector);
    return {
      setMenuCollapsed: (collapsed: boolean) => {
        store.updateState( { menuCollapsed: collapsed  });  
      },
      toggleMenu: () => {
        store.updateState( { menuCollapsed: !store.menuCollapsed()  });
      },
      syncFromCrossTab: () => {
        const loadedStore = loadGlobalStoreFromStorage();
        if (loadedStore && loadedStore.user && loadedStore.user.token) {
          // Actualización puramente pasiva en memoria: sin emitir userLoggedSubject ni llamar a APIs
          patchState(store, loadedStore);

          const tokenExpiration = Helpers.getAnyStorage<string>('roadit_token_expiration');
          const hasRefreshToken = !!store.user()['refresh-token'];

          if ((store.token() === "expired" || (tokenExpiration && new Date() > new Date(tokenExpiration))) && !hasRefreshToken) {
            console.log('Sesión expirada detectada en sincronización pasiva, realizando logout');
            store.logout();
          }
        } else {
          // Si otra pestaña cerró sesión o limpió storage, sincronizar logout
          if (store.isAuthenticated()) {
            store.logout();
          }
        }
      },
      initializeFromStorage: () => {
        const loadedStore = loadGlobalStoreFromStorage();
        if(loadedStore){
          patchState(store, loadedStore);
        } else {
          // Si no hay datos en storage y la pestaña estaba autenticada en memoria, deslogueamos (ej. logout en otra pestaña)
          if (store.isAuthenticated()) {
            store.logout();
            return;
          }
        }

        if(store.isAuthenticated()){
          // Emitir que el usuario está logueado para activar la sincronización
          userLoggedSubject.next(true);

          if (counterIntervalTimer) {
            clearInterval(counterIntervalTimer);
          }
          counterIntervalTimer = setInterval(() => { store.loadCounters().then(() => {}); }, 1000 * 60 * 5);
          store.loadCounters().then(() => {});
        }
        
        const tokenExpiration = Helpers.getAnyStorage<string>('roadit_token_expiration');
        const hasRefreshToken = !!store.user()['refresh-token'];

        // Only logout synchronously if there's NO refresh token to attempt renewal
        if ((store.token() === "expired" || (tokenExpiration && new Date() > new Date(tokenExpiration))) && !hasRefreshToken) {
            console.log('Sesión expirada, realizando logout automático');
            store.logout();
            return;
        }
      },

      tryAutoLogin: async () => {
        const userData = store.user();
        const token = store.token();
        const refreshToken = userData['refresh-token'];
        const rememberMe = store.rememberMe();
        const sessionActive = Helpers.getSessionStorage('roadit_session_tab_active');
        const tracker = injector.get(SessionTracker);

        const isTokenValid = token !== 'expired';
        const hasNoToken = !store.isAuthenticated();

        // 1. Token válido + rememberMe → login directo
        if (isTokenValid && rememberMe) {
          await store.setUser(userData, rememberMe, true);
          return true;
        }

        // 2. Token expirado + refresh-token → tryRefresh
        if (refreshToken && !isTokenValid) {
          const api = injector.get(ApiService);
          try {
            const response = await api.post<{ token: string; ['refresh-token']: string }>({
              url: EndPoints.refreshToken,
              body: { refresh: refreshToken },
              withoutAuth: true
            });

            if (response && response.token) {
              const updatedUser = new LoginResponseDto({
                ...userData,
                token: response.token,
                'refresh-token': response['refresh-token']
              });
              await store.setUser(updatedUser, rememberMe, true);
              return true;
            }
          } catch (error) {
            console.error('Error en auto-login:', error);
            // fall through to logout scenarios
          }
        }

        // 3. No token + rememberMe → esperar SessionTracker
        if (hasNoToken && rememberMe) {
          const hasOtherTab = await tracker.isAnotherTabOpen();
          if (hasOtherTab) {
            await store.setUser(userData, rememberMe, true);
            return true;
          }
          return false;
        }

        // 4. Token válido + !rememberMe + sessionActive → F5 preserve (no tracker query)
        if (isTokenValid && !rememberMe && sessionActive) {
          await store.setUser(userData, rememberMe, true);
          return true;
        }

        // 5. Token válido + !rememberMe + !sessionActive + otra tab existe
        if (isTokenValid && !rememberMe && !sessionActive) {
          const hasOtherTab = await tracker.isAnotherTabOpen();
          if (hasOtherTab) {
            Helpers.setSessionStorage('roadit_session_tab_active', 'true');
            await store.setUser(userData, rememberMe, true);
            return true;
          }
        }

        // Fallback: logout
        store.logout();
        return false;
      }
    };
  })
);

export const loadUserClientStore = (injector: Injector, clientUserId: number) => {
  runInInjectionContext(injector, () => {
    const clientUser = inject(UserClientStore);
    clientUser.loadById(clientUserId);
  });
}

export const clearGlobalStore = (injector: Injector) => {
  runInInjectionContext(injector, () => {
    const clientUser = inject(UserClientStore, { optional: true });
    const transport = inject(TransportStore, { optional: true });
    const drivers = inject(DriverStore  , { optional: true });
    if(clientUser) clientUser.clearStore();
    if(transport) transport.clearStore();
    if(drivers) drivers.clearStore();
  });
}

function saveGlobalStoreToStorage(store: any) {
  // 1. Configuración (Siempre en localStorage)
  const configProps = ['language', 'menuCollapsed'];
  const config: Record<string, unknown> = {};
  configProps.forEach(prop => config[prop] = store[prop]);
  Helpers.setStorage('roadit_global_config', config);

  // 2. Sesión (Siempre en localStorage — compartido entre tabs)
  const sessionProps = ['user', 'userFull', 'role', 'userClient', 'rememberMe', 'importDataUrls', 'budgetCounter', 'unreadNotifications', 'unreadCount', 'incidencesTypes', 'tags', 'usersAdmin', 'groups'];
  const session: Record<string, unknown> = {};
  sessionProps.forEach(prop => session[prop] = store[prop]);

  Helpers.setStorage('roadit_global_session', session);
  // Legacy cleanup: remove stale sessionStorage copies
  Helpers.removeSessionStorage('roadit_global_session');
  Helpers.removeSessionStorage('roadit_token_expiration');
}

function loadGlobalStoreFromStorage(): GlobalStateData | null {
  const config = Helpers.getStorage<Partial<GlobalStateData>>('roadit_global_config') || {};
  const session = Helpers.getAnyStorage<Partial<GlobalStateData>>('roadit_global_session') || {};
  
  if (Object.keys(config).length === 0 && Object.keys(session).length === 0) {
    return null;
  }

  const defaultState = new GlobalStateData();
  return {
    ...defaultState,
    ...config,
    ...session
  } as GlobalStateData;
}